import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { NearbyPlaceItem } from "@/components/place/types";
import { useBatchCovers } from "@/hooks/useBatchCovers";
import { useMemo } from "react";

interface NearbyPlaceRow {
  id: string;
  slug: string;
  name: string;
  landskap: string | null;
  municipality: string | null;
}

interface UseNearbyPlacesOptions {
  placeId?: string;
  region?: string;
  city?: string;
  limit?: number;
}

/**
 * Fetches nearby places based on region/city matching.
 * Images come from media_assets via batch covers.
 */
export function useNearbyPlaces({
  placeId,
  region,
  city,
  limit = 4,
}: UseNearbyPlacesOptions) {
  const query = useQuery({
    queryKey: ["nearby-places", placeId, region, city, limit],
    queryFn: async (): Promise<NearbyPlaceRow[]> => {
      if (!placeId) return [];

      let q = supabase
        .from("places")
        .select("id, slug, name, landskap, municipality")
        .eq("status", "published")
        .neq("id", placeId)
        .limit(limit + 4);

      if (region) {
        q = q.eq("landskap", region);
      }

      q = q.order("name", { ascending: true });

      const { data, error } = await q;

      if (error) {
        console.error("Error fetching nearby places:", error);
        throw error;
      }

      return (data as NearbyPlaceRow[]).slice(0, limit);
    },
    enabled: !!placeId,
  });

  const rows = query.data ?? [];
  const placeIds = useMemo(() => rows.map(r => r.id), [rows]);
  const { coverMap } = useBatchCovers('place', placeIds, 320);

  const nearbyPlaces: NearbyPlaceItem[] = useMemo(() => 
    rows.map((row) => ({
      id: row.id,
      slug: row.slug,
      name: row.name,
      region: row.landskap ?? undefined,
      city: row.municipality ?? undefined,
      imageUrl: coverMap.get(row.id) ?? undefined,
    })),
    [rows, coverMap]
  );

  return {
    nearbyPlaces,
    loading: query.isLoading,
    error: query.error?.message ?? null,
  };
}
